<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model="filter" :filters="filters" />
  </toolbar>

  <main class="px-12 mb-10 whitespace-pre">
    <ControlledTourMap :filter="filter" :initial-zoom="9" />
  </main>
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import Tabs from '@components/structure/navigation/Tabs.vue';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import { useI18n } from 'vue-i18n';
import { sub } from 'date-fns';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import ControlledTourMap from '@/components/utils/maps/ControlledTourMap.vue';
import {
  collOptionsProvider,
  type Filter,
  type ProviderParams,
} from '@/components/utils/table/hooks/provider';
import type {
  Region, Campaign,
} from '@/entities/Entities';

const { t } = useI18n();

const filter = ref<Filter>({
  date: {
    start: sub(new Date(), { days: 1 }),
    end: new Date(),
  },
});

const filters = [
  {
    key: 'date',
    type: 'datetime',
    group: t('pages.dashboard.filterGroups.general'),
    options: {
      label: t('statistics.overview.date'),
    },
  },
  {
    key: 'region',
    type: 'dropdown',
    group: t('pages.dashboard.filterGroups.general'),
    options: {
      options: collOptionsProvider<Region>('/regions', {
        sort: {
          sortKey: 'name',
          sortDirection: 'ASC',
        },
      }, (value) => ({
        label: value.name,
        value: value.id,
      })),
      label: t('statistics.overview.region'),
    },
  },
  {
    key: 'campaignGroup',
    type: 'dropdown',
    group: t('pages.dashboard.filterGroups.general'),
    options: {
      searchable: true,
      minChars: 2,
      filterResults: false,
      clearOnSelect: true,
      delay: 0,
      createOption: false,
      resolveOnLoad: true,
      noOptionsText: 'Suchbegriff eingeben',
      options: (query: string, select: any) => {
        const input = query || select.modelValue;
        if (!input) return Promise.resolve([]);
        const filterQuery: ProviderParams = {
          filter: query ? { name: input } : { id: select.modelValue },
        };
        return collOptionsProvider<Campaign>('/campaign_groups', filterQuery, (value) => ({
          label: value.name,
          value: value.id,
        }))();
      },
      label: t('statistics.overview.campaignGroup'),
    },
  },
];
</script>

<style lang="scss" scoped>
.icon {
  @apply flex items-center text-dark;
  @apply mb-12;
  @apply h-15;
  max-width: px(70);
}

.card-grid {
  @apply grid gap-x-4 gap-y-15;
  grid-template-columns: repeat(auto-fill, minmax(20rem, 1fr));
}

.data {
  @apply h1;
}

.card {
  @apply flex flex-col;
}

.card-content {
  @apply flex flex-col gap-y-2.5 h-full;
  min-height: px(200);
}
</style>
